import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LayoutDashboard, FileText, DollarSign, ClipboardList, CalendarPlus, ScanLine, TestTube, LogOut, Menu, X } from 'lucide-react';

const PortalLayout = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };

  const menuItems = [
    { icon: LayoutDashboard, label: t.navDashboard, path: '/portal/dashboard' },
    { icon: FileText, label: t.navTreatmentHistory, path: '/portal/history' },
    { icon: DollarSign, label: t.navPaymentRecords, path: '/portal/payments' },
    { icon: CalendarPlus, label: t.navBookAppointment, path: '/portal/book-appointment' },
    { icon: ClipboardList, label: t.navMedicalHistory, path: '/portal/medical-history' },
    { icon: ScanLine, label: t.navMyScans, path: '/portal/scans' },
    { icon: TestTube, label: t.navMedicalTests, path: '/portal/medical-tests' },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]" dir={t.dir} data-testid="portal-layout">
      <div className="bg-white border-b border-slate-200 fixed top-0 left-0 right-0 z-40">
        <div className="flex items-center justify-between px-4 lg:px-8 h-16">
          <div className="flex items-center">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden mr-4 text-[#1F2937]" data-testid="sidebar-toggle">
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div className="text-2xl font-bold text-[#0F4C5C] cursor-default" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.clinicName}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`hidden sm:block ${t.dir === 'rtl' ? 'text-left' : 'text-right'}`}>
              <div className="text-sm font-semibold text-[#1F2937]">{user?.name}</div>
              <div className="text-xs text-[#6B7280]">{user?.email}</div>
            </div>
            <button onClick={handleLogout} className="flex items-center text-[#6B7280] hover:text-[#0F4C5C] transition-colors" data-testid="logout-button">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex pt-16">
        <aside className={`fixed lg:sticky top-16 ${t.dir === 'rtl' ? 'right-0' : 'left-0'} h-[calc(100vh-4rem)] w-64 bg-white border-${t.dir === 'rtl' ? 'l' : 'r'} border-slate-200 transform transition-transform duration-300 z-30 ${sidebarOpen ? 'translate-x-0' : (t.dir === 'rtl' ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0')}`} data-testid="sidebar">
          <nav className="p-4">
            <div className="space-y-2">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-[#0F4C5C] text-white' : 'text-[#6B7280] hover:bg-[#F9FAFB]'}`}
                    data-testid={`nav-${item.path.split('/').pop()}`}>
                    <item.icon className={`w-5 h-5 ${t.dir === 'rtl' ? 'ml-3' : 'mr-3'}`} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        </aside>
        <main className="flex-1 lg:ml-0"><Outlet /></main>
      </div>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay"></div>}
    </div>
  );
};

export default PortalLayout;
